import { useEffect, useState } from "react";

import {
    ListContainer,
    ListContext,
    Page,
    PageRoute,
    SearchBar,
    Stack,
    buildPath,
    buildTemplatePath,
    pn
} from "@compass/core";

import { ListFooter } from "components";
import {
    Permission,
    DiplomaTypeListDto,
    DiplomaTypeListFilter,
    DiplomaTypeListResource
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { DiplomaTypeFilter } from "../DiplomaTypeFilter";
import { DiplomaTypeListPageHeader } from "./DiplomaTypeListPageHeader";
import { DiplomaTypeTable } from "./DiplomaTypeTable";

const routeName = "diplomaTypes";

export const diplomaTypeListPageRoute: PageRoute = {
    handle: "Diploma Types",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildPath(routeName),
};

export const DiplomaTypeListPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;

    const [listItems, setListItems] = useState<DiplomaTypeListDto[]>([]);
    const { listFilter, setListFilter } = useInitListFilter<DiplomaTypeListFilter>({
        sortObjects: [{ property: pn<DiplomaTypeListDto>("name"), isDesc: false }],
    });
    const [initListFilter] = useState<DiplomaTypeListFilter>(listFilter);
    const [total, setTotal] = useState<number>();
    const [resource, setResource] = useState<DiplomaTypeListResource>({});
    const [searchFocused, setSearchFocused] = useState<boolean>(false);

    const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
        listFilter: listFilter,
        setListFilter: setListFilter,
        fetchList: (listFilter) => {
            ApiService.diplomaTypes.getList(listFilter).then((response) => {
                setListItems(response);
            });
        },
        fetchTotal: (listFilter) => {
            setTotal(undefined);
            ApiService.diplomaTypes.getListTotal(listFilter).then((response) => {
                setTotal(response);
            });
        },
    });

    useEffect(() => {
        loadList(listFilter);
        ApiService.diplomaTypes.getListResource().then((response) => {
            setResource(response);
        });
    }, []);

    const listFilterNode = useListFilter<DiplomaTypeListFilter>({
        listFilter: listFilter,
        initListFilter: initListFilter,
        onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
        inputNode: (listFilter, setListFilter) => (
            <DiplomaTypeFilter
                filter={listFilter.diplomaTypeFilter || {}}
                setFilter={(filter) =>
                    setListFilter(
                        produce(listFilter, (draft) => {
                            draft.diplomaTypeFilter = filter;
                        })
                    )
                }
            />
        ),
    });

    return (
        <>
            <Page
                header={
                    <DiplomaTypeListPageHeader
                        rootPermission={ROOT_PERMISSION}
                        handle={diplomaTypeListPageRoute.handle}
                    />
                }
            >
                <ListContainer
                    fullHeight
                    header={
                        <Stack direction="row" justifyContent="space-between">
                            <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
                            {listFilterNode}
                        </Stack>
                    }
                    tableContainer={
                        <DiplomaTypeTable
                            rootPermission={ROOT_PERMISSION}
                            tableContainerRef={tableContainerRef}
                            diplomaTypes={listItems}
                            setDiplomaTypes={setListItems}
                            resource={resource}
                            searchFocused={searchFocused}
                            sortObjects={listFilter.sortObjects}
                            sort={applySort}
                            loadList={() => loadList(listFilter)}
                        />
                    }
                    footer={
                        <ListFooter
                            total={total}
                            size={listFilter.pageSize}
                            page={listFilter.page}
                            onPageChange={applyPage}
                            onSizeChange={applyPageSize}
                            onSortMethodChange={applySortMethod}
                        />
                    }
                />
            </Page>

            <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
                <Outlet />
            </ListContext.Provider>
        </>
    );
};
